/**
 * KPI Performance Module — API Service
 *
 * Typed API client for cycles, definitions, templates, assignments,
 * check-ins, reviews, comments, dashboards, and AI assistant.
 */
import apiClient from './client';

// ═══ Types ═══

export interface PerformanceCycle {
  id: string;
  org_id: string;
  name: string;
  cycle_type: 'quarterly' | 'semi_annual' | 'annual' | 'custom';
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'review' | 'closed';
  created_by: string | null;
  created_at: string;
  assignment_count?: number;
}

export interface KPIDefinition {
  id: string;
  org_id: string | null;
  name: string;
  description: string | null;
  business_question: string | null;
  category: string;
  kpi_type: 'quantitative' | 'qualitative' | 'binary' | 'milestone';
  unit: string;
  unit_label: string | null;
  directionality: 'higher_better' | 'lower_better' | 'target_exact';
  default_target: number | null;
  default_baseline: number | null;
  default_stretch: number | null;
  thresholds: { green_min?: number; yellow_min?: number; red_max?: number } | null;
  formula_description: string | null;
  financial_impact_type: string | null;
  financial_impact_description: string | null;
  cadence: string;
  owner_id: string | null;
  department_id: string | null;
  parent_kpi_id: string | null;
  tags: string[] | null;
  is_template: boolean;
  template_category: string | null;
  status: string;
  created_at: string;
}

export interface KPIAssignment {
  id: string;
  definition_id: string;
  cycle_id: string;
  assignee_id: string;
  setter_id: string | null;
  verifier_id: string | null;
  target_value: number | null;
  baseline_value: number | null;
  stretch_value: number | null;
  weight: number;
  current_value: number | null;
  current_percentage: number | null;
  financial_impact_target: number | null;
  financial_impact_actual: number | null;
  status: string;
  notes: string | null;
  created_at: string;
  // Enriched
  definition_name?: string;
  definition_category?: string;
  definition_unit?: string;
  definition_unit_label?: string;
  definition_directionality?: string;
  definition_kpi_type?: string;
  definition_thresholds?: Record<string, number>;
  definition_cadence?: string;
  assignee_name?: string;
  assignee_email?: string;
  cycle_name?: string;
  cycle_start_date?: string;
  cycle_end_date?: string;
  check_in_count?: number;
  last_check_in?: string;
  check_in_history?: Array<{ date: string; value: number; percentage: number | null }>;
}

export interface KPIEvidence {
  id: string;
  check_in_id: string;
  s3_key: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  uploaded_at: string;
}

export interface KPICheckIn {
  id: string;
  assignment_id: string;
  submitted_by: string;
  submitted_at: string;
  value: number | null;
  percentage: number | null;
  notes: string | null;
  evidence_urls: string[] | null;
  ai_summary: string | null;
  financial_impact_value: number | null;
  status: string;
  reviewer_notes: string | null;
  reviewed_by: string | null;
  reviewer_name?: string;
  submitter_name?: string;
  evidence: KPIEvidence[];
}

export interface KPIReview {
  id: string;
  assignment_id: string;
  reviewer_id: string;
  review_type: string;
  score: number | null;
  score_label: string | null;
  strengths: string | null;
  improvements: string | null;
  comments: string | null;
  submitted_at: string;
  reviewer_name?: string;
}

export interface KPIComment {
  id: string;
  assignment_id: string;
  author_id: string;
  text: string;
  parent_comment_id: string | null;
  created_at: string;
  author_name?: string;
  author_avatar?: string;
}

export interface KPIStatusSummary {
  total: number;
  on_track: number;
  at_risk: number;
  behind: number;
  not_started: number;
  completed: number;
}

export interface DashboardMyKPIs {
  cycle: PerformanceCycle | null;
  assignments: KPIAssignment[];
  summary: KPIStatusSummary;
  overall_score: number | null;
}

export interface TeamMemberKPIs {
  user_id: string;
  display_name: string | null;
  email: string;
  title: string | null;
  assignments: KPIAssignment[];
  summary: KPIStatusSummary;
  overall_score: number | null;
}

export interface DashboardTeam {
  cycle: PerformanceCycle | null;
  members: TeamMemberKPIs[];
  team_summary: KPIStatusSummary;
  ai_narrative: string | null;
}

export interface DepartmentKPIs {
  department_id: string | null;
  department_name: string;
  member_count: number;
  kpi_count: number;
  avg_score: number | null;
  summary: KPIStatusSummary;
}

export interface DashboardOrg {
  cycle: PerformanceCycle | null;
  org_summary: KPIStatusSummary;
  overall_score: number | null;
  departments: DepartmentKPIs[];
  members: TeamMemberKPIs[];
}

export interface EvidenceUploadResponse {
  id: string;
  s3_key: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  extracted_text: string | null;
}

export interface AISummaryResponse {
  summary: string;
  highlights: string[];
  concerns: string[];
  recommendations: string[];
}

export interface AISmartFillResponse {
  suggested_value: number | null;
  confidence: string | null;
  narrative: string | null;
  extracted_data: Record<string, any> | null;
}

// ═══ API Calls ═══

// ── Cycles ──

export const listCycles = async (): Promise<PerformanceCycle[]> => {
  const { data } = await apiClient.get('/api/kpi/cycles');
  return data;
};

export const createCycle = async (cycle: {
  name: string; cycle_type: string; start_date: string; end_date: string;
}): Promise<PerformanceCycle> => {
  const { data } = await apiClient.post('/api/kpi/cycles', cycle);
  return data;
};

export const updateCycle = async (
  cycleId: string, updates: { name?: string; status?: string }
): Promise<PerformanceCycle> => {
  const { data } = await apiClient.patch(`/api/kpi/cycles/${cycleId}`, updates);
  return data;
};

// ── Definitions ──

export const listDefinitions = async (category?: string): Promise<KPIDefinition[]> => {
  const params = category ? { category } : {};
  const { data } = await apiClient.get('/api/kpi/definitions', { params });
  return data;
};

export const createDefinition = async (defn: {
  name: string; description?: string; category?: string; kpi_type?: string;
  unit?: string; unit_label?: string; directionality?: string;
  default_target?: number; default_baseline?: number; cadence?: string;
  department_id?: string; template_id?: string; tags?: string[];
  thresholds?: Record<string, number>; formula_description?: string;
  business_question?: string;
  financial_impact_type?: string; financial_impact_description?: string;
}): Promise<KPIDefinition> => {
  const { data } = await apiClient.post('/api/kpi/definitions', defn);
  return data;
};

export const updateDefinition = async (
  defId: string, updates: Partial<KPIDefinition>
): Promise<KPIDefinition> => {
  const { data } = await apiClient.patch(`/api/kpi/definitions/${defId}`, updates);
  return data;
};

export const deleteDefinition = async (defId: string): Promise<void> => {
  await apiClient.delete(`/api/kpi/definitions/${defId}`);
};

export const listTemplates = async (category?: string): Promise<KPIDefinition[]> => {
  const params = category ? { category } : {};
  const { data } = await apiClient.get('/api/kpi/templates', { params });
  return data;
};

// ── Assignments ──

export const listAssignments = async (
  cycleId: string, scope?: string, assigneeId?: string
): Promise<KPIAssignment[]> => {
  const params: Record<string, string> = { cycle_id: cycleId, scope: scope || 'own' };
  if (assigneeId) params.assignee_id = assigneeId;
  const { data } = await apiClient.get('/api/kpi/assignments', { params });
  return data;
};

export const getAssignment = async (id: string): Promise<KPIAssignment> => {
  const { data } = await apiClient.get(`/api/kpi/assignments/${id}`);
  return data;
};

export const createAssignment = async (assignment: {
  definition_id: string; cycle_id: string; assignee_id: string;
  target_value?: number; baseline_value?: number; weight?: number; notes?: string;
  financial_impact_target?: number; verifier_id?: string;
}): Promise<KPIAssignment> => {
  const { data } = await apiClient.post('/api/kpi/assignments', assignment);
  return data;
};

export const bulkCreateAssignments = async (payload: {
  definition_id: string; cycle_id: string; assignee_ids: string[];
  target_value?: number; baseline_value?: number; weight?: number;
}): Promise<KPIAssignment[]> => {
  const { data } = await apiClient.post('/api/kpi/assignments/bulk', payload);
  return data;
};

export const updateAssignment = async (
  id: string, updates: Partial<KPIAssignment>
): Promise<KPIAssignment> => {
  const { data } = await apiClient.patch(`/api/kpi/assignments/${id}`, updates);
  return data;
};

export const approveAssignment = async (id: string): Promise<KPIAssignment> => {
  const { data } = await apiClient.post(`/api/kpi/assignments/${id}/approve`);
  return data;
};

export const deleteAssignment = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/kpi/assignments/${id}`);
};

export const archiveAssignment = async (id: string): Promise<KPIAssignment> => {
  const { data } = await apiClient.post(`/api/kpi/assignments/${id}/archive`);
  return data;
};

export const deleteCycle = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/kpi/cycles/${id}`);
};

// ── Check-ins ──

export const createCheckIn = async (
  assignmentId: string, checkIn: {
    value?: number; notes?: string; evidence_urls?: string[];
    evidence_ids?: string[]; financial_impact_value?: number;
  }
): Promise<KPICheckIn> => {
  const { data } = await apiClient.post(`/api/kpi/assignments/${assignmentId}/check-in`, checkIn);
  return data;
};

export const listCheckIns = async (assignmentId: string): Promise<KPICheckIn[]> => {
  const { data } = await apiClient.get(`/api/kpi/assignments/${assignmentId}/check-ins`);
  return data;
};

export const verifyCheckIn = async (
  checkInId: string, status: string, notes?: string
): Promise<KPICheckIn> => {
  const { data } = await apiClient.patch(`/api/kpi/check-ins/${checkInId}/verify`, { status, notes });
  return data;
};

export const uploadEvidence = async (
  checkInId: string, file: File
): Promise<EvidenceUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post(
    `/api/kpi/check-ins/${checkInId}/evidence`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

export const getEvidenceDownloadUrl = async (
  evidenceId: string
): Promise<{ download_url: string; filename: string }> => {
  const { data } = await apiClient.get(`/api/kpi/evidence/${evidenceId}/download`);
  return data;
};

// ── Reviews ──

export const createReview = async (
  assignmentId: string, review: {
    review_type: string; score?: number; score_label?: string;
    strengths?: string; improvements?: string; comments?: string;
  }
): Promise<KPIReview> => {
  const { data } = await apiClient.post(`/api/kpi/assignments/${assignmentId}/review`, review);
  return data;
};

export const listReviews = async (assignmentId: string): Promise<KPIReview[]> => {
  const { data } = await apiClient.get(`/api/kpi/assignments/${assignmentId}/reviews`);
  return data;
};

// ── Comments ──

export const createComment = async (
  assignmentId: string, comment: { text: string; parent_comment_id?: string }
): Promise<KPIComment> => {
  const { data } = await apiClient.post(`/api/kpi/assignments/${assignmentId}/comments`, comment);
  return data;
};

export const listComments = async (assignmentId: string): Promise<KPIComment[]> => {
  const { data } = await apiClient.get(`/api/kpi/assignments/${assignmentId}/comments`);
  return data;
};

// ── Dashboards ──

export const getMyDashboard = async (cycleId?: string): Promise<DashboardMyKPIs> => {
  const params = cycleId ? { cycle_id: cycleId } : {};
  const { data } = await apiClient.get('/api/kpi/dashboard/my', { params });
  return data;
};

export const getTeamDashboard = async (cycleId?: string): Promise<DashboardTeam> => {
  const params = cycleId ? { cycle_id: cycleId } : {};
  const { data } = await apiClient.get('/api/kpi/dashboard/team', { params });
  return data;
};

export const getOrgDashboard = async (
  cycleId?: string, departmentId?: string
): Promise<DashboardOrg> => {
  const params: Record<string, string> = {};
  if (cycleId) params.cycle_id = cycleId;
  if (departmentId) params.department_id = departmentId;
  const { data } = await apiClient.get('/api/kpi/dashboard/org', { params });
  return data;
};

// ── AI Assistant ──

export const aiChat = async (
  message: string, contextType?: string, contextId?: string
): Promise<{ reply: string; conversation_id: string }> => {
  const { data } = await apiClient.post('/api/kpi/ai/chat', {
    message, context_type: contextType || 'general', context_id: contextId,
  });
  return data;
};

export const aiSmartFill = async (
  assignmentId: string, evidenceText?: string
): Promise<AISmartFillResponse> => {
  const { data } = await apiClient.post(`/api/kpi/ai/smart-fill/${assignmentId}`, null, {
    params: evidenceText ? { evidence_text: evidenceText } : {},
  });
  return data;
};

export const aiTeamSummary = async (cycleId?: string): Promise<AISummaryResponse> => {
  const params = cycleId ? { cycle_id: cycleId } : {};
  const { data } = await apiClient.get('/api/kpi/ai/summary/team', { params });
  return data;
};

export const aiAssignmentSummary = async (assignmentId: string): Promise<AISummaryResponse> => {
  const { data } = await apiClient.get(`/api/kpi/ai/summary/assignment/${assignmentId}`);
  return data;
};
